/*
 * lunna-email-popup.js — when Lunna Notes is allowed to ask, and how it behaves
 * once it is open.
 *
 * The rules it enforces are the ones a visitor would want enforced: never
 * before they have seen the page, never twice in a session, never again for a
 * day after they said no, and effectively never again once they have signed
 * up. Everything is decided from this browser's own storage — there is no
 * profile, no identifier and no request to anything.
 *
 * Focus handling is Origin's. trapFocus/removeTrapFocus come from global.js,
 * which is loaded on every page, so the dialog behaves like every other modal
 * in the theme instead of inventing its own conventions.
 */
(function () {
  'use strict';

  /* v3 deliberately gets a clean first impression. The previous generic
     invitation wrote long cooldowns while its trigger was being tested; those
     records must not prevent the redesigned experience from ever appearing. */
  var STORE_KEY = 'lunna:notes:v3';
  var SESSION_KEY = 'lunna:notes:v3:session';
  var SUBMIT_KEY = 'lunna:notes:v3:submitting';
  var MOBILE_QUERY = '(max-width: 749px)';
  var DAY = 24 * 60 * 60 * 1000;

  /* Preview switch. `?popup=1` on any storefront URL opens the invitation at
     once and ignores every reason to stay quiet, so the design can be checked
     without clearing storage by hand.

     It is strictly read-only, and the guard lives inside the three writers
     below rather than at their call sites: that way a call site added later
     cannot forget it, and "preview never writes" holds by construction instead
     of by review. */
  var forcePreview = false;
  try {
    forcePreview = new URLSearchParams(window.location.search).get('popup') === '1';
  } catch (error) {
    forcePreview = false;
  }

  /* Storage is a convenience, not a dependency. Private modes and blocked
     cookies throw on access; when that happens the popup still works, it just
     cannot remember — which is the safe direction to fail in for the session
     guard below, and the reason it is kept separate from the long cooldowns. */
  function readStore() {
    try {
      return JSON.parse(window.localStorage.getItem(STORE_KEY)) || {};
    } catch (error) {
      return {};
    }
  }

  function writeStore(patch) {
    if (forcePreview) return;

    try {
      var next = readStore();
      Object.keys(patch).forEach(function (key) {
        next[key] = patch[key];
      });
      window.localStorage.setItem(STORE_KEY, JSON.stringify(next));
    } catch (error) {
      /* Nothing to do: the visit simply is not remembered. */
    }
  }

  function readSession(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function writeSession(key, value) {
    if (forcePreview) return;

    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      /* Ignored, as above. */
    }
  }

  function clearSession(key) {
    if (forcePreview) return;

    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      /* Ignored, as above. */
    }
  }

  class LunnaEmailPopup extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.dialog = this.querySelector('[data-lunna-notes-dialog]');
      this.form = this.querySelector('form');
      this.openedBy = null;
      this.timer = null;
      this.scrollHandler = null;
      this.scrollQueued = false;

      this.isEditor = Boolean(window.Shopify && window.Shopify.designMode);
      this.isPreview = this.hasAttribute('data-preview');

      this.querySelectorAll('[data-lunna-notes-close]').forEach(
        function (button) {
          button.addEventListener('click', this.dismiss.bind(this));
        }.bind(this)
      );

      /* Welcome offer: copy the code without leaving the dialog. */
      this.querySelectorAll('[data-lunna-notes-copy]').forEach(function (button) {
        button.addEventListener('click', function () {
          var code = button.getAttribute('data-lunna-notes-copy');
          var done = function () {
            button.textContent = button.getAttribute('data-copied-label') || button.textContent;
          };
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(code).then(done, function () {});
          }
        });
      });

      var overlay = this.querySelector('[data-lunna-notes-overlay]');
      if (overlay) overlay.addEventListener('click', this.dismiss.bind(this));

      this.addEventListener('keydown', this.onKeydown.bind(this));

      if (this.form) {
        /* Marks this browser as the one that submitted from the popup. Both
           newsletter forms on the page report posted_successfully after either
           is sent, so without this the footer signup would pop the dialog open
           on the next render. */
        this.form.addEventListener('submit', function () {
          writeSession(SUBMIT_KEY, '1');
        });
      }

      if (this.querySelector('[data-lunna-notes-success]')) {
        this.handleReturningSuccess();
        return;
      }

      if (this.isEditor) {
        if (this.isPreview) {
          this.open();
        } else {
          this.arm();
        }
        return;
      }

      this.arm();
    }

    disconnectedCallback() {
      this.disarm();
    }

    /* Shopify sent the visitor back after accepting the address. Reopen only if
       this browser submitted from here, then record the long quiet period. */
    handleReturningSuccess() {
      if (this.isEditor) {
        if (this.isPreview) this.open({ success: true });
        return;
      }

      if (readSession(SUBMIT_KEY) !== '1') return;
      clearSession(SUBMIT_KEY);

      writeStore({ subscribedUntil: Date.now() + this.days('subscribed-days', 180) * DAY });
      this.open({ success: true });
    }

    days(attribute, fallback) {
      var value = parseInt(this.getAttribute('data-' + attribute), 10);
      return isNaN(value) ? fallback : value;
    }

    number(attribute, fallback) {
      var value = parseFloat(this.getAttribute('data-' + attribute));
      return isNaN(value) ? fallback : value;
    }

    get isMobile() {
      return window.matchMedia(MOBILE_QUERY).matches;
    }

    /* Every reason not to ask, in one place. */
    get suppressed() {
      if (forcePreview) return false;

      /* Theme editing must remain testable. It follows the normal timing when
         Preview is off, and dismissal storage is never allowed to silence it. */
      if (this.isEditor) return false;

      if (readSession(SESSION_KEY)) return true;

      var store = readStore();
      var now = Date.now();
      if (store.subscribedUntil && store.subscribedUntil > now) return true;
      if (store.dismissedUntil && store.dismissedUntil > now) return true;

      return false;
    }

    arm() {
      if (this.suppressed) return;

      /* Preview asks for the dialog now rather than on a delay or a scroll. */
      if (forcePreview) {
        this.open();
        return;
      }

      var seconds = this.isMobile
        ? this.number('delay-mobile', 55)
        : this.number('delay-desktop', 40);

      this.timer = window.setTimeout(this.trigger.bind(this), seconds * 1000);

      /* A passive listener that coalesces into one rAF and takes itself off the
         moment it fires. An IntersectionObserver sentinel was the alternative,
         but its position would have to be recomputed every time lazy content
         changed the document height, which is more work than this, not less. */
      this.scrollHandler = function () {
        if (this.scrollQueued) return;
        this.scrollQueued = true;
        window.requestAnimationFrame(this.checkScroll.bind(this));
      }.bind(this);

      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }

    disarm() {
      if (this.timer) window.clearTimeout(this.timer);
      this.timer = null;

      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
        this.scrollHandler = null;
      }
    }

    checkScroll() {
      this.scrollQueued = false;

      var scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;

      var percent = (window.scrollY / scrollable) * 100;
      var target = this.isMobile
        ? this.number('scroll-mobile', 60)
        : this.number('scroll-desktop', 50);

      if (percent >= target) this.trigger();
    }

    /* Never on top of something else. Cart drawer, quick add, product media and
       the menu drawer all lock the page the same way, so one check covers them
       — and rather than give up, the popup waits and asks again later. */
    trigger() {
      if (this.suppressed) {
        this.disarm();
        return;
      }

      if (document.body.classList.contains('overflow-hidden')) {
        this.disarm();
        this.timer = window.setTimeout(this.trigger.bind(this), 8000);
        return;
      }

      this.disarm();
      this.open();
    }

    open(options) {
      if (this.hasAttribute('open')) return;

      var settings = options || {};

      this.openedBy = document.activeElement;
      this.hidden = false;
      this.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
      document.body.classList.add('lunna-notes-open');

      if (settings.success) this.classList.add('is-success');

      /* One frame between display and the class, so the entry transition has a
         start state to move away from. */
      window.requestAnimationFrame(
        function () {
          this.classList.add('is-open');
        }.bind(this)
      );

      /* Focus lands on the dialog itself, which is labelled by the heading, so
         the title is announced without a ring appearing on a non-interactive
         element. The first Tab from there reaches the close button. */
      if (typeof trapFocus === 'function') {
        trapFocus(this.dialog, this.dialog);
      } else if (this.dialog) {
        this.dialog.focus();
      }

      this.dispatchEvent(new CustomEvent('lunna:notes:opened', { bubbles: true }));
    }

    /* Closing is always the visitor's decision, so it always counts as one: the
       session guard stops it returning on the next page, and the cooldown stops
       it returning until tomorrow. Editor and `?popup=1` preview write neither. */
    dismiss() {
      if (!this.hasAttribute('open')) return;

      this.classList.remove('is-open');
      this.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('lunna-notes-open');

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.openedBy);
      } else if (this.openedBy && this.openedBy.focus) {
        this.openedBy.focus();
      }

      if (!this.isEditor) {
        writeSession(SESSION_KEY, '1');
        if (!this.classList.contains('is-success')) {
          writeStore({ dismissedUntil: Date.now() + this.days('cooldown-days', 1) * DAY });
        }
      }

      window.setTimeout(
        function () {
          if (!this.hasAttribute('open')) this.hidden = true;
        }.bind(this),
        400
      );
    }

    onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.stopPropagation();
        this.dismiss();
      }
    }
  }

  if (!window.customElements.get('lunna-email-popup')) {
    window.customElements.define('lunna-email-popup', LunnaEmailPopup);
  }
})();
